'use client'

// Property card component for landlord dashboard
// Displays property information with actions for editing, deleting, and viewing applications
import { useState } from 'react'
import { Property } from '@/types'
import { useCurrency } from '@/lib/currency'

interface PropertyCardProps {
  property: Property
  onEdit?: (property: Property) => void
  onDelete?: (propertyId: string) => void
  onViewApplications?: (propertyId: string) => void
  onToggleAvailability?: (propertyId: string, isAvailable: boolean) => void
  showActions?: boolean
}

export default function PropertyCard({ 
  property, 
  onEdit, 
  onDelete, 
  onViewApplications,
  onToggleAvailability,
  showActions = true 
}: PropertyCardProps) {
  const [imageIndex, setImageIndex] = useState(0)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const { format } = useCurrency()

  // Format price using currency context
  const formatPrice = (price: number) => format(price)

  // Handle image navigation
  const nextImage = () => {
    if (property.images && property.images.length > 1) {
      setImageIndex((prev) => (prev + 1) % property.images.length)
    }
  }

  const prevImage = () => {
    if (property.images && property.images.length > 1) {
      setImageIndex((prev) => (prev - 1 + property.images.length) % property.images.length)
    }
  }

  // Handle delete confirmation
  const handleDelete = () => {
    if (showDeleteConfirm) {
      onDelete?.(property.id)
      setShowDeleteConfirm(false)
    } else {
      setShowDeleteConfirm(true)
    }
  }

  // Get verification status
  const isVerified = property.is_verified === true
  const isAvailable = property.is_available !== false

  // Format location from address object or location string
  const getLocationDisplay = () => {
    if (typeof property.location === 'string' && property.location.trim()) {
      return property.location
    }
    
    if (property.address && typeof property.address === 'object') {
      const addr = property.address as { street?: string; city?: string; state?: string; zipCode?: string; country?: string }
      const parts = []
      if (addr.street) parts.push(addr.street)
      if (addr.city) parts.push(addr.city)
      if (addr.state) parts.push(addr.state)
      if (parts.length > 0) {
        return parts.join(', ')
      }
    }
    
    if (property.city || property.state) {
      return [property.city, property.state].filter(Boolean).join(', ') || 'Location not specified'
    }
    
    return 'Location not specified'
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden hover:shadow-xl hover:border-emerald-200 transition-all duration-300 group">
      {/* Image Section */}
      <div className="relative h-56 bg-stone-100 overflow-hidden">
        {property.images && property.images.length > 0 ? (
          <>
            <img
              src={property.images[imageIndex]}
              alt={property.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            
            {/* Status Overlays */}
            <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
              <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold shadow-lg ${
                isVerified ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'
              }`}>
                {isVerified ? '✓ Verified' : '⏳ Pending'}
              </span>
              
              {/* Rental Type Badge */}
              {property.rentalType && (
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold shadow-lg ${
                  property.rentalType === 'short-term'
                    ? 'bg-amber-500 text-white'
                    : 'bg-stone-800 text-white'
                }`}>
                  {property.rentalType === 'short-term' ? '🏠 Shortlet' : '📅 Long-term'}
                </span>
              )}
              
              {property.videos && property.videos.length > 0 && (
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-purple-500 text-white shadow-lg">
                  🎥 {property.videos.length} Video{property.videos.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            
            {/* Image Navigation */}
            {property.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-black/60 backdrop-blur text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/80 transition-all shadow-lg"
                  aria-label="Previous image"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-black/60 backdrop-blur text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/80 transition-all shadow-lg"
                  aria-label="Next image"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                
                {/* Image Indicators */}
                <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-1.5">
                  {property.images.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === imageIndex ? 'bg-white w-6' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-400 relative">
            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            
            {/* Status Overlay for No Image */}
            <div className="absolute top-3 left-3">
              <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold shadow-lg ${
                isVerified ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'
              }`}>
                {isVerified ? '✓ Verified' : '⏳ Pending'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-6">
        {/* Title and Price */}
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-lg font-display text-stone-900 line-clamp-2 flex-1 pr-2">
            {property.title}
          </h3>
          <div className="text-right flex-shrink-0">
            <p className="text-xl font-display text-emerald-600 font-bold">
              {formatPrice(property.price)}
            </p>
            <p className="text-xs text-stone-500">
              {property.rentalType === 'short-term' ? '/day' : '/month'}
            </p>
          </div>
        </div>

        {/* Status Badges */}
        <div className="mb-3 space-y-2">
          <div className="flex flex-wrap gap-2">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
              isVerified ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-amber-100 text-amber-800 border border-amber-200'
            }`}>
              {isVerified ? '✓ Verified - Live' : '⏳ Pending Review'}
            </span>
            
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
              isAvailable ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-red-100 text-red-800 border border-red-200'
            }`}>
              {isAvailable ? 'Available' : 'Unavailable'}
            </span>
          </div>
        </div>

        {/* Location */}
        <p className="text-stone-600 mb-3 flex items-center text-sm">
          <svg className="w-4 h-4 mr-1.5 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="line-clamp-1">{getLocationDisplay()}</span>
        </p>

        {/* Description */}
        <p className="text-stone-600 text-sm mb-4 line-clamp-2">
          {property.description}
        </p>

        {/* Action Buttons */}
        {showActions && (
          <div className="flex flex-wrap gap-2 pt-4 border-t border-stone-100">
            {onViewApplications && (
              <button
                onClick={() => onViewApplications(property.id)}
                className="flex-1 min-w-[140px] bg-emerald-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/25"
              >
                View Applications
              </button>
            )}
            
            {onToggleAvailability && (
              <button
                onClick={() => onToggleAvailability(property.id, !isAvailable)}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 whitespace-nowrap border-2 ${
                  isAvailable 
                    ? 'text-amber-700 border-amber-300 hover:bg-amber-50' 
                    : 'text-emerald-700 border-emerald-300 hover:bg-emerald-50'
                }`}
              >
                {isAvailable ? 'Mark Unavailable' : 'Mark Available'}
              </button>
            )}

            {onEdit && (
              <button
                onClick={() => !isVerified && onEdit(property)}
                disabled={isVerified}
                title={isVerified ? 'Cannot edit approved properties' : 'Edit property'}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 whitespace-nowrap border-2 ${
                  isVerified
                    ? 'text-stone-400 border-stone-200 cursor-not-allowed bg-stone-50'
                    : 'text-stone-700 border-stone-300 hover:bg-stone-50'
                }`}
              >
                {isVerified ? 'Approved' : 'Edit'}
              </button>
            )}
            
            {onDelete && (
              <button
                onClick={handleDelete}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 whitespace-nowrap border-2 ${
                  showDeleteConfirm
                    ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
                    : 'text-red-600 border-red-300 hover:bg-red-50'
                }`}
              >
                {showDeleteConfirm ? 'Confirm Delete' : 'Delete'}
              </button>
            )}
          </div>
        )}

        {/* Verification Notice */}
        {isVerified && (
          <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
            <p className="text-xs text-emerald-700 font-medium flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              This property is approved and protected from editing
            </p>
          </div>
        )}
        
        {/* Listed Date */}
        <p className="text-xs text-stone-400 mt-4">
          Listed {property.created_at 
            ? (() => {
                try {
                  const date = new Date(property.created_at)
                  return isNaN(date.getTime()) ? 'Recently' : date.toLocaleDateString()
                } catch {
                  return 'Recently'
                }
              })()
            : 'Recently'}
        </p>
      </div>
    </div>
  )
}
